<template>
  <div class="shared-link-list">
    <span class="title">{{ $t("sharedLinkListOnSlideMenu.title") }}</span>
    <div class="list">
      <transition-group name="tran" v-if="sharedLinkList.length">
        <div v-for="item in sharedLinkList" :key="item.sharedLinkId" class="item" @click="select(item.sharedLinkId)">
          <div class="item-title ellipsis">
            {{ item.name || "NO TITLE" }}
          </div>
          <dot-icon v-if="isIncludesUnreadComment(item.sharedLinkId)" class="notification" />
        </div>
      </transition-group>
      <div v-else class="description">
        <div>{{ $t("sharedLinkListOnSlideMenu.noLinks") }}</div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Vue, { PropType } from "vue";
import { SharedLink, User } from "@/lib/models";
import { Comment as CommentDexie } from "@/lib/indexeddb";
import { compareAsc } from "date-fns";
import DotIcon from "@/components/atoms/DotIcon.vue";

export default Vue.extend<{}, Methods, Computed, Props>({
  components: { DotIcon },
  props: {
    novelId: {
      type: String as PropType<string>,
    },
    kind: {
      type: String,
      required: true,
    },
  },
  methods: {
    async select(sharedLinkId) {
      this.$store.dispatch("sharedLinkModule/selectSharedLink", sharedLinkId);
    },
  },
  computed: {
    user() {
      return this.$store.getters["userModule/user"] as User;
    },
    sharedLinkList() {
      const sharedLinkList = this.$store.getters["sharedLinkModule/sharedLinkList"] as SharedLink[];
      const enabledList = sharedLinkList
        .filter((sharedLink) => sharedLink.enabled)
        .sort((x, y) => compareAsc(x.createdAt!, y.createdAt!));
      const disabledList = sharedLinkList
        .filter((sharedLink) => !sharedLink.enabled)
        .sort((x, y) => compareAsc(x.createdAt!, y.createdAt!));
      return enabledList.concat(disabledList);
    },
    isIncludesUnreadComment() {
      return (sharedLinkId) => {
        const { novelId, kind } = this;
        const { userId } = this.user as User;
        const unreadCommentList = this.$store.getters["sharedLinkModule/unreadCommentList"](
          userId,
          novelId,
          kind,
          sharedLinkId
        ) as CommentDexie[];
        return !!unreadCommentList.length;
      };
    },
  },
});

interface Props {
  novelId: string;
  kind: string;
}

interface Methods {
  select: (sharedLinkId: string) => void;
}

interface Computed {
  user: User;
  sharedLinkList: SharedLink[];
  isIncludesUnreadComment: (sharedLinkId: string) => boolean;
}
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";
@mixin link {
  background: transparent;
  border: none;
  cursor: pointer;
}

.shared-link-list {
  width: 100%;
  padding-left: 16px;
  padding-right: 4px;
  border: 1px solid var(--color-border);
  border-top-left-radius: 10px;
  background-color: var(--color-background-normal);

  height: 100%;
  display: flex;
  flex-direction: column;

  .title {
    font-size: var(--font-size-md);
    font-weight: var(--font-weight-bold);
    margin: 12px 0;
    user-select: none;
    color: var(--color-text-normal);
  }
}

.list {
  flex-grow: 1;
  overflow-y: auto;

  .item {
    box-sizing: border-box;
    height: 50px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 15px;
    cursor: pointer;
    border-bottom: 1px solid var(--color-border);
    color: var(--color-text-normal);
    position: relative;
    @include hover-overlay;

    &.active {
      background-color: var(--color-surface-subtle);

      &::after {
        display: none;
      }
    }

    .item-title {
      width: 250px;
      font-size: 1.1em;
    }

    .notification {
      fill: var(--color-warning);
      width: 1em;
      height: 1em;
    }
  }

  .description {
    margin: 20px 15px;
    padding: 20px 10px;
    color: var(--color-text-low);
  }
}
</style>
